import { useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import {
  Controller,
  useFieldArray,
  useFormState,
  useWatch,
  type Control,
  type UseFormSetValue,
} from 'react-hook-form'
import { ENROLLMENT_STATUSES, type PayerEnrollmentRow } from '@behuman/core-contract'
import { ACCENT } from '@/components/ui/primaryButton.tokens'
import type { PractitionerOpsOrganization } from '@/hooks/usePractitionerOpsScope'
import { SupportingDocumentSlot } from '@/pages/Super/components/LicensesField'
import { states } from '@/utils/states'
import { findPayerOption, payerOptionsFor, type PayerOption } from './PayerEnrollmentsField.utils'

// The card's row while it is being edited: the contract row with the required selects allowed
// to hold the empty string a fresh row starts with, plus the Supporting Document picked but
// not yet saved. The page's Save turns the pending file into a Binary and sends its reference
// as `document`; the contract never sees the File.
export type PayerEnrollmentRowValue = Omit<Partial<PayerEnrollmentRow>, 'state' | 'status'> & {
  state?: PayerEnrollmentRow['state'] | ''
  status?: PayerEnrollmentRow['status'] | ''
  pendingFile?: File | null
}
export type PayerEnrollmentsFormValues = { payerEnrollments: PayerEnrollmentRowValue[] }

/** The card's header: "Payer · State" as far as either is filled in, else "New enrollment". */
function enrollmentHeading(row: PayerEnrollmentRowValue | undefined): string {
  const stateName = states.find((s) => s.value === row?.state)?.label
  const parts = [row?.payer?.trim(), stateName].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : 'New enrollment'
}

const fieldGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--mantine-spacing-xs)',
  alignItems: 'start' as const,
}

/**
 * The Payer Enrollments tab's card list: one card per Payer Enrollment (see the glossary),
 * modelled on the license card. Add appends a blank card; Remove asks first, since the row's
 * edits go with it. The Billing Organization is chosen from the caller's Provider Operations
 * scope, preselected when that scope has exactly one Organization. The Payer is picked from
 * the insurance payer catalog's rows for the chosen State, never typed, so every row carries
 * the catalog payer's ID; a State change drops a Payer the new State does not list.
 */
export function PayerEnrollmentsField({
  control,
  setValue,
  organizations,
  payerOptions,
}: {
  control: Control<PayerEnrollmentsFormValues>
  setValue: UseFormSetValue<PayerEnrollmentsFormValues>
  organizations: readonly PractitionerOpsOrganization[]
  payerOptions: readonly PayerOption[]
}) {
  const name = 'payerEnrollments'
  const { fields, append, remove } = useFieldArray({ control, name })
  // The row the removal dialog is open for, by id, with the name the dialog shows.
  const [removing, setRemoving] = useState<{ id: string; label: string } | null>(null)
  const rows = useWatch({ control, name }) ?? []
  // The cross-row rules report on `payer` (a duplicate), which the Payer Select shows along
  // with its own `payerId` error, there being no separate control for the name.
  const { errors } = useFormState({ control })
  const organizationOptions = organizations.map((org) => ({ value: org.id, label: org.name }))

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">
        Payer Enrollments
      </Text>

      {fields.length === 0 && (
        <Text size="xs" c="dimmed">
          No Payer Enrollments yet.
        </Text>
      )}

      {fields.map((field, index) => {
        const row = rows[index]
        const status = ENROLLMENT_STATUSES.find((s) => s.value === row?.status)
        // What the payer knows the Billing Organization by, to copy into its portal. A blank
        // ("—") means the Organization has none on file: a super admin fills it there.
        const organization = organizations.find((org) => org.id === row?.billingOrganizationId)
        const textField = (
          key: 'productNetwork' | 'verifiedBy' | 'approvalReference',
          label: string,
        ) => (
          <Controller
            name={`${name}.${index}.${key}`}
            control={control}
            render={({ field: input, fieldState }) => (
              <TextInput
                {...input}
                value={input.value ?? ''}
                label={label}
                placeholder={label}
                error={fieldState.error?.message}
              />
            )}
          />
        )
        const dateField = (
          key: 'effectiveDate' | 'endDate' | 'verifiedDate',
          label: string,
          { maxDate }: { maxDate?: Date } = {},
        ) => (
          <Controller
            name={`${name}.${index}.${key}`}
            control={control}
            render={({ field: input, fieldState }) => (
              <DatePickerInput
                {...input}
                value={input.value ?? null}
                label={label}
                placeholder={label}
                clearable
                clearButtonProps={{ 'aria-label': `Clear ${label}` }}
                maxDate={maxDate}
                error={fieldState.error?.message}
              />
            )}
          />
        )
        return (
          <Paper key={field.id} withBorder p="sm">
            <Group justify="space-between" align="center" mb="xs">
              <Group gap="xs">
                <Text size="sm" fw={500} c="dimmed">
                  {enrollmentHeading(row)}
                </Text>
                {status && (
                  <Badge color="gray" variant="light">
                    {status.label}
                  </Badge>
                )}
              </Group>
              <ActionIcon
                variant="subtle"
                color="red"
                // Confirmed first: the row's edits go with it, and nothing short of a reload
                // (which drops the other rows' edits too) brings them back.
                onClick={() =>
                  setRemoving({
                    id: field.id,
                    label:
                      row?.payer || row?.state
                        ? `${enrollmentHeading(row)} enrollment`
                        : `enrollment ${index + 1}`,
                  })
                }
                aria-label={`Remove enrollment ${index + 1}`}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>

            <Box style={fieldGrid}>
              <Controller
                name={`${name}.${index}.billingOrganizationId`}
                control={control}
                render={({ field: input, fieldState }) => (
                  <Select
                    {...input}
                    value={input.value || null}
                    label="Billing Organization"
                    placeholder="Billing Organization"
                    data={organizationOptions}
                    searchable
                    allowDeselect={false}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={`${name}.${index}.state`}
                control={control}
                render={({ field: input, fieldState }) => (
                  <Select
                    {...input}
                    value={input.value || null}
                    onChange={(value) => {
                      input.onChange(value ?? '')
                      // The Payer list is the new State's: a pick it does not carry goes.
                      if (
                        row?.payerId &&
                        !findPayerOption(payerOptions, { ...row, state: value ?? '' })
                      ) {
                        setValue(`${name}.${index}.payer`, '', { shouldDirty: true })
                        setValue(`${name}.${index}.payerId`, '', { shouldDirty: true })
                      }
                    }}
                    label="State"
                    placeholder="State"
                    data={states}
                    searchable
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name={`${name}.${index}.payerId`}
                control={control}
                render={({ field: input, fieldState }) => (
                  <Select
                    ref={input.ref}
                    name={input.name}
                    onBlur={input.onBlur}
                    // Blank for a payer the catalog does not list in this State, as for a row
                    // saved before the Payer became pick-only: it has to be picked again.
                    value={(row && findPayerOption(payerOptions, row)?.value) ?? null}
                    onChange={(value) => {
                      const option = payerOptions.find((candidate) => candidate.value === value)
                      input.onChange(option?.payerId ?? '')
                      setValue(`${name}.${index}.payer`, option?.payer ?? '', {
                        shouldDirty: true,
                      })
                    }}
                    label="Payer"
                    placeholder={row?.state ? 'Payer' : 'Select a State first'}
                    disabled={!row?.state}
                    data={payerOptionsFor(payerOptions, row?.state).map(({ value, label }) => ({
                      value,
                      label,
                    }))}
                    searchable
                    nothingFoundMessage="No payer found"
                    allowDeselect={false}
                    error={
                      fieldState.error?.message ?? errors.payerEnrollments?.[index]?.payer?.message
                    }
                  />
                )}
              />
            </Box>
            <Box style={fieldGrid} mt="xs">
              {textField('productNetwork', 'Product / Network')}
              <Controller
                name={`${name}.${index}.status`}
                control={control}
                render={({ field: input, fieldState }) => (
                  <Select
                    {...input}
                    value={input.value || null}
                    label="Enrollment Status"
                    placeholder="Enrollment Status"
                    data={[...ENROLLMENT_STATUSES]}
                    allowDeselect={false}
                    error={fieldState.error?.message}
                  />
                )}
              />
              {dateField('effectiveDate', 'Effective date')}
            </Box>
            <Box style={fieldGrid} mt="xs">
              {dateField('endDate', 'End date')}
              {/* A verification cannot have happened yet: the picker stops at today. */}
              {dateField('verifiedDate', 'Verified date', { maxDate: new Date() })}
              {textField('verifiedBy', 'Verified by')}
            </Box>
            <Box style={fieldGrid} mt="xs">
              {textField('approvalReference', 'Payer approval reference')}
            </Box>
            {organization && (
              <Text size="xs" c="dimmed" mt="xs">
                {organization.name} · Group NPI {organization.npi ?? '—'} · TIN{' '}
                {organization.taxId ?? '—'}
              </Text>
            )}

            <Stack gap={4} mt="xs">
              <Text size="sm" fw={500}>
                Supporting document
              </Text>
              <SupportingDocumentSlot
                document={row?.document}
                pendingFile={row?.pendingFile}
                onPick={(file) =>
                  setValue(`${name}.${index}.pendingFile`, file, { shouldDirty: true })
                }
                onClear={() => {
                  // A pending pick is dropped; a saved document is cleared on the next Save.
                  if (row?.pendingFile) {
                    setValue(`${name}.${index}.pendingFile`, null, { shouldDirty: true })
                  } else {
                    setValue(`${name}.${index}.document`, null, { shouldDirty: true })
                  }
                }}
              />
            </Stack>
          </Paper>
        )
      })}

      {/* After the cards, where the new card appears, so a long list needs no scroll back up. */}
      <Group>
        <Button
          size="xs"
          variant="subtle"
          color={ACCENT}
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            append(
              {
                billingOrganizationId: organizations.length === 1 ? organizations[0].id : '',
                state: '',
                payer: '',
                payerId: '',
                productNetwork: '',
                status: '',
                effectiveDate: undefined,
                endDate: null,
                verifiedDate: null,
                verifiedBy: '',
                approvalReference: '',
              },
              // Don't autofocus the new row's select — focusing it pops the dropdown open.
              { shouldFocus: false },
            )
          }
        >
          Add enrollment
        </Button>
      </Group>

      <Modal
        opened={removing !== null}
        onClose={() => setRemoving(null)}
        title="Remove enrollment"
        centered
      >
        <Stack>
          <Text>Remove the {removing?.label}? It is gone for good once you Save.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                // Found again by id: a clean form is reset by a background refetch, which
                // re-keys every row, so a row that moved or vanished is not removed.
                const at = fields.findIndex((row) => row.id === removing?.id)
                if (at >= 0) remove(at)
                setRemoving(null)
              }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
